import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ZpHeadLayout from './ZpHeadLayout';
import { db } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaFileAlt,
    FaFilePdf,
    FaSearch
} from 'react-icons/fa';

const ZpHeadLotteryList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('lottery-list-society');
    const department = isSociety ? 'society' : 'subhe';

    const [works, setWorks] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [lotteryDates, setLotteryDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [loading, setLoading] = useState(true);

    // Suspension Modal State
    const [showSuspensionModal, setShowSuspensionModal] = useState(false);
    const [suspensionReason, setSuspensionReason] = useState('');
    const [activeSuspensionItem, setActiveSuspensionItem] = useState(null);

    useEffect(() => {
        setLoading(true);

        // Fetch Lottery Dates from generatework
        const genWorkRef = ref(db, 'ekamwatapzp/generatework');
        onValue(genWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const dates = Object.keys(snapshot.val());
                setLotteryDates(dates.reverse());
            }
        });

        // Fetch Works for metadata fallback
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            flattenedWorks.push({ id: key, meetingNumber: val.meetingNumber || "General", ...val });
                        } else {
                            Object.entries(val).forEach(([id, value]) => {
                                flattenedWorks.push({ id, meetingNumber: key, ...value });
                            });
                        }
                    }
                });
                setWorks(flattenedWorks);
            }
        });

        // Fetch Registrations
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const checkRegDone = () => {
            if (engDone && socDone) {
                const regs = department === 'society' 
                    ? Object.entries(socData).map(([key, val]) => ({ ...val, _key: key, displayType: 'मजूर संख्या' }))
                    : Object.entries(engData).map(([key, val]) => ({ ...val, _key: key, displayType: 'सु.बे. अभियंता' }));
                
                setRegistrations(regs);
                setLoading(false);
            }
        };

        onValue(engRef, (snap) => { engData = snap.val() || {}; engDone = true; checkRegDone(); });
        onValue(socRef, (snap) => { socData = snap.val() || {}; socDone = true; checkRegDone(); });

    }, [department]);

    const handleSearch = () => {
        setAppliedDate(selectedDate);
    };

    const handleToggleSuspension = (item) => {
        if (!item.isSuspended) {
            // Turning OFF (Suspending) - Open Modal
            setActiveSuspensionItem(item);
            setSuspensionReason(item.suspensionReason || '');
            setShowSuspensionModal(true);
        } else {
            // Turning ON (Unsuspending) - Update directly
            const regPath = department === 'society' ? 'SocietyRegistrations' : 'EngineerRegistrations';
            const lotteryRef = ref(db, `ekamwatapzp/${regPath}/${item.id}/GeneratedLottery/${appliedDate}`);
            update(lotteryRef, {
                isSuspended: false,
                suspensionReason: null
            });
        }
    };

    const confirmSuspension = () => {
        if (!activeSuspensionItem) return;
        const regPath = department === 'society' ? 'SocietyRegistrations' : 'EngineerRegistrations';
        const lotteryRef = ref(db, `ekamwatapzp/${regPath}/${activeSuspensionItem.id}/GeneratedLottery/${appliedDate}`);
        
        update(lotteryRef, {
            isSuspended: true,
            suspensionReason: suspensionReason
        }).then(() => {
            setShowSuspensionModal(false);
            setActiveSuspensionItem(null);
            setSuspensionReason('');
        });
    };

    // Marathi numerals for display
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    // Process data to find actual winners for the selected date
    const displayData = useMemo(() => {
        if (!appliedDate) return [];

        const results = [];
        registrations.forEach(reg => {
            if (reg.GeneratedLottery && reg.GeneratedLottery[appliedDate]) {
                const lotteryData = reg.GeneratedLottery[appliedDate];
                const workId = lotteryData.id || lotteryData.workNumber || lotteryData.workCode;
                
                const paymentInfo = (reg.work_payment && reg.work_payment[appliedDate]) || {};
                
                results.push({
                    id: reg._key,
                    applicantName: reg.name || `${reg.firstName || ''} ${reg.middleName || ''} ${reg.surname || ''}`.trim() || reg.fullName || reg.societyName || "-",
                    phone: reg.whatsappNo || reg.mobile || reg.phoneNumber || "-",
                    taluka: reg.taluka || "-",
                    workId: workId,
                    workName: lotteryData.workName || "काम उपलब्ध नाही",
                    amount: lotteryData.tenderAmount || "-",
                    challanStatus: paymentInfo.status || 'Pending',
                    challanUrl: paymentInfo.challanUrl || null,
                    isSuspended: lotteryData.isSuspended || false,
                    suspensionReason: lotteryData.suspensionReason || ''
                });
            }
        });
        return results;
    }, [registrations, appliedDate]);

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                <div className="animate-spin text-[#22165f] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 px-2 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#22165f] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                        {department === 'society' ? "मजूर संख्या लॉटरी यादी" : "सु.बे. लॉटरी यादी"}
                    </h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600 font-black">{department === 'society' ? "मजूर संख्या लॉटरी यादी" : "सु.बे. लॉटरी यादी"}</span>
                </div>
            </div>

            {/* Filter Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8">
                <div className="flex flex-col gap-4">
                    <label className="text-sm font-semibold text-gray-700">
                        लॉटरीची तारीख निवडा <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative w-full md:w-80">
                            <select 
                                className="w-full bg-white border border-gray-300 rounded-lg py-3 px-4 focus:ring-2 focus:ring-blue-500/20 outline-none transition font-medium appearance-none text-gray-700"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            >
                                <option value="">लॉटरीची तारीख निवडा</option>
                                {lotteryDates.map(date => (
                                    <option key={date} value={date}>{date}</option>
                                ))}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <FaSearch size={14} />
                            </div>
                        </div>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#5c73db] hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-bold shadow-sm transition transform active:scale-95 text-sm"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#fcfdff] border-b border-gray-100">
                            <tr className="text-[12px] font-black text-gray-500 uppercase tracking-wide">
                                <th className="px-6 py-5 border-r border-gray-50 w-16 text-center">#</th>
                                <th className="px-6 py-5 border-r border-gray-50 min-w-[220px]">
                                    {department === 'society' ? "संस्थेचे नाव" : "अर्जाचे नाव"}
                                </th>
                                <th className="px-6 py-5 border-r border-gray-50 min-w-[150px]">फोन नंबर</th>
                                <th className="px-6 py-5 border-r border-gray-50 min-w-[150px]">तालुका</th>
                                <th className="px-6 py-5 border-r border-gray-100 min-w-[450px]">कामाचे नाव</th>
                                <th className="px-6 py-5 border-r border-gray-50 min-w-[250px]">वर्क / निविदा फॉर्म फी चलन</th>
                                <th className="px-6 py-5 min-w-[180px]"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 text-[13.5px]">
                            {displayData.map((item, index) => (
                                <tr key={index} className={`transition-colors group ${item.isSuspended ? 'bg-red-50/50 grayscale-[0.2]' : 'hover:bg-blue-50/20'}`}>
                                    <td className="px-6 py-6 border-r border-gray-50 text-center font-bold text-gray-400">
                                        {index + 1}
                                    </td>
                                    <td className="px-6 py-6 border-r border-gray-50 font-black text-gray-700">
                                        <div className="flex flex-col gap-1">
                                            {item.applicantName}
                                            {item.isSuspended && (
                                                <span className="text-[10px] text-red-500 bg-red-100 px-2 py-0.5 rounded-full w-fit">निलंबन केलेले</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 border-r border-gray-50 font-bold text-gray-600">
                                        {item.phone}
                                    </td>
                                    <td className="px-6 py-6 border-r border-gray-50 font-bold text-gray-600">
                                        {item.taluka}
                                    </td>
                                    <td className="px-6 py-6 border-r border-gray-50">
                                        <div className="flex flex-col gap-1.5">
                                            <span className="font-bold text-gray-800 leading-tight">
                                                {item.workId} - {item.workName}
                                            </span>
                                            <span className="text-gray-500 font-bold text-[11px] uppercase tracking-wider">
                                                Rs. - {item.amount}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 border-r border-gray-50">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className={`font-black uppercase text-[11px] tracking-widest ${
                                                item.challanUrl ? 'text-amber-500' : (item.challanStatus === 'Approved' ? 'text-green-600' : 'text-gray-400')
                                            }`}>
                                                {item.challanUrl ? 'Pending' : 'Not Uploaded'}
                                            </span>
                                            {item.challanUrl && (
                                                <a href={item.challanUrl} target="_blank" rel="noreferrer" className="bg-red-50 text-red-500 p-2 rounded-lg border border-red-100 hover:bg-red-500 hover:text-white transition shadow-sm">
                                                    <FaFilePdf size={16} />
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="flex items-center justify-between gap-6">
                                            <button className="text-[#5c73db] hover:text-blue-700 font-black text-[11px] uppercase tracking-tight text-left">
                                                Extend <br/> Date
                                            </button>
                                            <div 
                                                onClick={() => handleToggleSuspension(item)}
                                                className={`relative inline-flex items-center cursor-pointer group w-12 h-6 rounded-full transition-colors duration-300 ${item.isSuspended ? 'bg-gray-300' : 'bg-green-500'}`}
                                            >
                                                <div className={`absolute w-4 h-4 bg-white rounded-full shadow transition-transform duration-300 transform ${item.isSuspended ? 'translate-x-1' : 'translate-x-7'}`}></div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-6 py-32 text-center bg-gray-50/50">
                                        <div className="flex flex-col items-center gap-4 text-gray-400">
                                            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100">
                                                <FaSearch size={30} className="text-gray-200" />
                                            </div>
                                            <p className="font-bold italic text-sm">निकषांनुसार कोणतीही माहिती सापडली नाही.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Suspension Reason Modal */}
            {showSuspensionModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-in fade-in zoom-in duration-300">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">निलंबनाचे कारण</h3>
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-0.5">Reason for Suspension</p>
                            </div>
                            <button 
                                onClick={() => setShowSuspensionModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition"
                            >
                                <FaSearch className="rotate-45" size={20} />
                            </button>
                        </div>
                        <div className="p-8">
                            <label className="block text-sm font-bold text-gray-700 mb-3">
                                निलंबनाचे कारण प्रविष्ट करा <span className="text-red-500">*</span>
                            </label>
                            <textarea 
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 min-h-[120px] focus:ring-2 focus:ring-blue-500/20 outline-none transition font-medium text-gray-700 resize-none"
                                placeholder="येथे कारण लिहा..."
                                value={suspensionReason}
                                onChange={(e) => setSuspensionReason(e.target.value)}
                            />
                            <p className="text-[10px] text-gray-400 font-bold mt-2 uppercase italic">* हे कारण संबंधित लाभार्थ्याच्या डॅशबोर्डवर दर्शविले जाईल.</p>
                        </div>
                        <div className="p-6 bg-gray-50/50 flex gap-3">
                            <button 
                                onClick={() => setShowSuspensionModal(false)}
                                className="flex-1 px-6 py-3 bg-white border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition active:scale-95"
                            >
                                रद्द करा
                            </button>
                            <button 
                                onClick={confirmSuspension}
                                disabled={!suspensionReason.trim()}
                                className="flex-1 px-6 py-3 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white rounded-xl font-bold transition shadow-lg shadow-red-500/20 active:scale-95"
                            >
                                निलंबित करा
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                
                /* Selection Style */
                select {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 1rem center;
                    background-size: 1.25rem;
                }
            `}</style>
        </div>
    );
};

export default ZpHeadLotteryList;
